import { isImage } from 'shared_DEPRECATED/components/File/utils';
import { dateUtils, firstLetterUpperCase } from 'shared_DEPRECATED/utils';
import { DAILY_STATUS_VALUES } from 'shared_DEPRECATED/utils/config';

import {
  CHALLENGE_FIELD_TYPES,
  CHALLENGE_TYPES,
  CUSTOM_JOURNAL_PROMPT_TAG,
  DAY_VALUE_MAP,
  DEFAULT_TOTAL_AMOUNT,
  EVERYDAY,
  TARGET_TYPES,
  WEEKDAY_TARGET_DEFAULT,
  WEEKDAYS_VALUE_ON_TYPE_MAP,
} from 'features/challenge/config';
import { TARGET_PERIOD } from 'features/challenge/config/types';

export const dimensionFirstLetterToUpperCase = (dimension) =>
  dimension
    .split(' & ')
    .map((word) => firstLetterUpperCase(word))
    .join(' & ');

export const mapWeekdayTargetToWeeklyTarget = (
  weekdayTarget,
  sprintWeeksCount
) => {
  weekdayTarget = weekdayTarget || {};
  const oneWeekTarget = Object.values(weekdayTarget).reduce((acc, val) => {
    if (val) {
      acc++;
    }

    return acc;
  }, 0);

  return new Array(sprintWeeksCount).fill(oneWeekTarget);
};

export const getDefaultChallengeType = (availableChallengeFields) =>
  Object.entries(availableChallengeFields).reduce(
    (challengeType, [field, isAvailable]) =>
      isAvailable ? CHALLENGE_FIELD_TYPES[field] : challengeType,
    null
  );

export const getChallengeTypeByTarget = (target) =>
  target.type === TARGET_TYPES.DUE_DATE
    ? CHALLENGE_TYPES.NON_REPEATING
    : CHALLENGE_TYPES.REPEATING;

const setDefaultChallengeTarget = (challengeTarget) =>
  challengeTarget.type === TARGET_TYPES.DUE_DATE && !challengeTarget.dueDate
    ? {
        type: TARGET_TYPES.DUE_DATE,
        dueDate: dateUtils().format('YYYY-MM-DD'),
      }
    : challengeTarget;

export const mapChallenge = (challenge) => ({
  ...challenge,
  type: challenge.type ?? getChallengeTypeByTarget(challenge.target),
  target: setDefaultChallengeTarget(challenge.target),
});

const getWeekdayTargetValue = (weekdayTarget) =>
  Object.entries(weekdayTarget).reduce((acc, [day, value]) => {
    if (value) {
      acc += DAY_VALUE_MAP[day];
    }

    return acc;
  }, 0);

export const onFrequencyCheckboxChange = (day, value, setValue) => (event) => {
  const { checked } = event.target;
  const isOneDayChecked = Object.values(DAY_VALUE_MAP).find(
    (dayValue) => dayValue === getWeekdayTargetValue(value)
  );

  !(isOneDayChecked && !checked) &&
    setValue({
      type: TARGET_TYPES.WEEKDAY,
      weekday: {
        ...WEEKDAY_TARGET_DEFAULT,
        ...value,
        [day.toLowerCase()]: checked,
      },
    });
};

export const getWeekdayTargetText = (weekdayTarget, format = 'weekdaysMin') => {
  const weekdayTargetValue = getWeekdayTargetValue(weekdayTarget);

  const weekdayType = WEEKDAYS_VALUE_ON_TYPE_MAP[weekdayTargetValue];

  if (weekdayType) {
    return weekdayType;
  }

  return dateUtils
    .weekDays('weekdaysShort')
    .map((day) => weekdayTarget[day.toLowerCase()])
    .map((isDayChecked, index) =>
      isDayChecked ? dateUtils.weekDays(format)[index] : isDayChecked
    )
    .filter(Boolean)
    .join('/');
};

export const getChallengeFrequencyText = (
  challengeTarget,
  sprintDuration = 6,
  weekdaysFormat = 'weekdaysMin'
) => {
  if (!challengeTarget?.type) {
    return 'Select frequency';
  }

  if (!Object.values(TARGET_TYPES).includes(challengeTarget.type)) {
    return '';
  }

  if (challengeTarget.type === TARGET_TYPES.DUE_DATE) {
    return dateUtils(challengeTarget.dueDate).format('MMM D, YYYY');
  }

  if (challengeTarget.type === TARGET_TYPES.WEEKLY) {
    const uniqueFrequencies = new Set(challengeTarget.weekly);
    const isConsistentFrequency = uniqueFrequencies.size === 1;
    const [singleFrequency] = uniqueFrequencies;

    if (isConsistentFrequency) {
      if (singleFrequency === dateUtils.WEEK_DAYS_COUNT) {
        return EVERYDAY;
      }

      const dayLabel = singleFrequency === 1 ? 'day' : 'days';
      return `${singleFrequency} ${dayLabel} / week`;
    }

    return challengeTarget.weekly.slice(0, sprintDuration).join('/');
  }

  return getWeekdayTargetText(challengeTarget.weekday, weekdaysFormat);
};

export const getDailyStatusImageUrl = (medias) => {
  const lastMedia = medias?.at(-1);
  if (!lastMedia) {
    return '';
  }

  return isImage(lastMedia.contentType)
    ? lastMedia.url
    : lastMedia.thumbnail.url;
};

export const getDailyStatusSetOnDate = (dailyStatuses) => {
  const statuses = dailyStatuses;

  for (const date in statuses) {
    if (
      statuses[date]?.status &&
      statuses[date].status !== DAILY_STATUS_VALUES.EMPTY
    ) {
      return date;
    }
  }
  return undefined;
};

export const getDailyStatusMediaList = (target, formattedDate) => {
  const notes =
    target.type === TARGET_TYPES.DUE_DATE
      ? target?.notesPerDate?.[formattedDate]
      : target.dailyStatus?.[formattedDate]?.notes;

  return notes?.flatMap((note) => note.media || []) || [];
};

export const getChallengePerWeekTargetAmount = ({ dailyStatusList = [] }) => {
  for (let i = dailyStatusList.length - 1; i >= 0; i--) {
    if (dailyStatusList[i]?.amount > 0) {
      return dailyStatusList[i].amount;
    }
  }

  return DEFAULT_TOTAL_AMOUNT;
};

export const getChallengePerSprintTargetAmount = ({ target }) => {
  const lastStatusDate =
    target.dailyStatus && Object.keys(target.dailyStatus).at(-1);

  return (
    target.dailyStatus?.[lastStatusDate]?.amountTotal ?? DEFAULT_TOTAL_AMOUNT
  );
};

export const isChallengeFrequencyConfigurable = ({ challengeTargetPeriod }) => {
  return (
    !challengeTargetPeriod || challengeTargetPeriod === TARGET_PERIOD.PER_DAY
  );
};

export const isChallengeCurated = (owner) => Boolean(owner?.userId);

export const extractPromptFromTags = (tags) => {
  const promptTag = tags.find((tag) =>
    tag.startsWith(CUSTOM_JOURNAL_PROMPT_TAG)
  );

  return promptTag ? promptTag.substring(7) : '';
};

export const getBetweenTargetDescription = (
  minValue,
  maxValue,
  unit,
  period
) => {
  const unitsText = unit || 'times';
  const periodText = period.replace('per_', '');

  return `between ${minValue} and ${maxValue} ${unitsText} per ${periodText} to succeed`;
};

export const getConstraintMode = (minValue, maxValue) => {
  const isAtLeast = minValue !== null && maxValue === null;
  const isNoMoreThan = maxValue !== null && minValue === null;
  const isBetween = minValue !== null && maxValue !== null;
  const backwardCompatible = minValue == null && maxValue == null;

  return {
    isAtLeast,
    isNoMoreThan,
    isBetween,
    backwardCompatible,
    isAtLeastMode: isAtLeast || backwardCompatible,
  };
};
